import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import IdentityCard from '../components/IdentityCard';

/* ─────────────────────────────────────────────
   SCROLL REVEAL
───────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('cs-revealed'); }),
      { threshold: 0.07 }
    );
    document.querySelectorAll('.cs-reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ─────────────────────────────────────────────
   COVER IMAGE FADE ON SCROLL
───────────────────────────────────────────── */
function useCoverFade(ref: React.RefObject<HTMLElement | null>) {
  const [opacity, setOpacity] = useState(1);
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      // fade out as the section scrolls off the top
      const progress = Math.min(Math.max(-rect.top / (rect.height * 0.6), 0), 1);
      setOpacity(1 - progress);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [ref]);
  return opacity;
}

/* ─────────────────────────────────────────────
   GIF REPLAY — changing `key` forces React to
   unmount + remount <img>, restarting the GIF
───────────────────────────────────────────── */
function ReplayGif({ src, alt, style }: { src: string; alt: string; style?: React.CSSProperties }) {
  const [imgKey, setImgKey] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setImgKey(k => k + 1); },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={wrapRef}>
      <img key={imgKey} src={src} alt={alt} style={style} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   HIGHLIGHT SWEEP — marker sweep on scroll.
   Uses setTimeout so the initial background-size:0%
   is always painted before the transition fires,
   even for elements visible on page load.
───────────────────────────────────────────── */
function Hl({ children, color = 'yellow' }: { children: React.ReactNode; color?: 'yellow' | 'blue' | 'pink' }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          // Always delay so browser paints background-size:0% first
          setTimeout(() => setActive(true), 180);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const cls = `hl hl-${color} ${active ? 'hl-active' : ''}`;
  return <span ref={ref} className={cls}>{children}</span>;
}

/* ─────────────────────────────────────────────
   COUNTING STAT
───────────────────────────────────────────── */
function CountStat({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const steps = 40; const duration = 900;
        const inc = target / steps; let cur = 0;
        const id = setInterval(() => {
          cur = Math.min(cur + inc, target);
          setVal(Math.round(cur));
          if (cur >= target) clearInterval(id);
        }, duration / steps);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return (
    <div ref={ref} style={{ fontSize: '3rem', fontWeight: 800, color: '#0071e3', lineHeight: 1 }}>
      {val}{suffix}
    </div>
  );
}

/* ─────────────────────────────────────────────
   FEATURE TABS
───────────────────────────────────────────── */
const ASSETS_FEATURES = [
  ['Tri-axis navigation', 'N cycles Y / Z / X. Arrow keys move within the axis. Ctrl+Shift+Arrow jumps segments. Position preserved across all axis switches.'],
  ['Multi-dim sonification', 'Pitch = Y value (200–1200 Hz). Timbre = X position (sine / triangle / square). Duration = Z depth. Boundary sounds at dataset edges.'],
  ['Surface + Point mode', 'Press 1 or 2. Wireframe cell or individual data point. All state preserved between modes.'],
  ['Autoplay (P key)', 'Sweeps the whole dataset at 8 pts/sec (Point) or 4 rects/sec (Surface).'],
  ['Intelligent autoplay (I)', 'Speed adapts dynamically based on data curvature — slows near peaks.'],
  ['Text verbosity (T)', 'Verbose → Terse → Super-terse. Delivered via TTS and ARIA live regions simultaneously.'],
  ['Review mode (V)', 'Scrollable log of every explored point. Compatible with any screen reader.'],
  ['Gamepad support', 'Left joystick pans, right joystick zooms. Requested and implemented by Aziz during early testing.'],
  ['CSV / JSON upload', 'User-supplied data works with all features from day one.'],
  ['Dark mode (M)', 'High contrast toggle. No state reset.'],
];
const CHI_ADDITIONS = [
  ['Reference sonification (0)', 'Plays the origin (0,0,0) as a fixed reference tone. Proposed by Ken and Sile in Session 1.'],
  ['Replay current point (.)', 'Re-hears the current position without moving. Pairs with the 0-key for relative comparison.'],
  ['Stereo panning', 'Sound pans left to right as X increases. Proposed by Venkatesh in Session 1.'],
  ['Volumetric audio (Z key)', 'Convolution reverb with 10–90ms pre-delay scales with Z depth. JooYoung\'s proposal at Session 2.'],
  ['Configurable buffer (D + G)', 'Drag-select a region, save it, replay sequentially or as an aggregated mean. Proposed by JooYoung.'],
  ['Jump-to-peak (J)', 'Outlier detection finds the top and bottom 10 peaks, cycles them with distinct tones.'],
  ['AI chat (scoped)', 'Gemini 2.5 Pro. Buffer selection scopes the natural language query to the selected region.'],
];

function FeatureTabs() {
  const [tab, setTab] = useState<0 | 1>(0);
  const rows = tab === 0 ? ASSETS_FEATURES : CHI_ADDITIONS;
  return (
    <div className="cs-reveal" style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {(['ASSETS 2025 — base features', 'CHI 2026 — additions'] as const).map((label, i) => (
          <button key={label} onClick={() => setTab(i as 0 | 1)}
            style={{ padding: '0.45rem 1.1rem', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.82rem',
              background: tab === i ? '#0071e3' : '#f0f0f0',
              color: tab === i ? '#fff' : '#555', transition: 'all 0.2s' }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: tab === 0 ? '#eef4ff' : '#fce7f3' }}>
              <th style={th}>Feature</th><th style={th}>How it works</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r[0]} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap', color: '#1d1d1f' }}>{r[0]}</td>
                <td style={td}>{r[1]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   KEYBINDINGS TABLE
───────────────────────────────────────────── */
const KEYBINDINGS = [
  { k: '1 / 2', a: 'Switch Surface / Point mode' },
  { k: 'N', a: 'Cycle navigation axis (Y → Z → X)' },
  { k: '↑ ↓ ← →', a: 'Move within the active axis' },
  { k: 'Ctrl+Shift+↑↓', a: 'Jump between data segments' },
  { k: 'P', a: 'Start / stop autoplay' },
  { k: 'I', a: 'Intelligent autoplay (speed adapts to curvature)' },
  { k: 'J', a: 'Jump to nearest peak or valley' },
  { k: 'S', a: 'Toggle sonification on / off' },
  { k: 'T', a: 'Cycle text verbosity (Verbose → Terse → Super-terse)' },
  { k: 'V', a: 'Open review mode (full point log)' },
  { k: '0', a: 'Play reference tone at origin' },
  { k: '.', a: 'Replay current position' },
  { k: 'D + arrows + Enter', a: 'Drag-select a region into the buffer' },
  { k: 'G', a: 'Toggle buffer playback mode' },
  { k: 'M', a: 'Toggle dark / light mode' },
  { k: 'H', a: 'Open help menu' },
  { k: 'Z', a: 'Toggle volumetric audio (CHI addition)' },
];
function KeyTable() {
  const [open, setOpen] = useState(false);
  const rows = open ? KEYBINDINGS : KEYBINDINGS.slice(0, 6);
  return (
    <div className="cs-reveal" style={{ marginTop: '1.2rem' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead><tr style={{ background: '#f5f5f7' }}>
            <th style={th}>Key</th><th style={th}>Action</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.k} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ ...td, fontFamily: 'monospace', color: '#0071e3', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.k}</td>
                <td style={td}>{r.a}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => setOpen(o => !o)}
        style={{ marginTop: '0.6rem', background: 'none', border: '1px solid #d6d6d6', borderRadius: 6,
          padding: '0.35rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer', color: '#555' }}>
        {open ? 'Show fewer' : `Show all ${KEYBINDINGS.length} keybindings`}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════ */
const DIXTRALCaseStudy: React.FC = () => {
  useReveal();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const coverRef = useRef<HTMLElement>(null);
  const coverOpacity = useCoverFade(coverRef);

  return (
    <div className="App" style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        /* ── Scroll reveal ── */
        .cs-reveal { opacity: 0; transform: translateY(26px); transition: opacity 0.65s cubic-bezier(.22,1,.36,1), transform 0.65s cubic-bezier(.22,1,.36,1); }
        .cs-reveal.cs-revealed { opacity: 1; transform: translateY(0); }
        .cs-reveal.d1 { transition-delay: 0.08s; }
        .cs-reveal.d2 { transition-delay: 0.16s; }
        .cs-reveal.d3 { transition-delay: 0.24s; }
        .cs-reveal.d4 { transition-delay: 0.32s; }
        .cs-reveal.d5 { transition-delay: 0.40s; }

        /* ── Highlight sweep ── */
        .hl {
          background-repeat: no-repeat;
          background-position: left center;
          background-size: 0% 88%;
          transition: background-size 0.75s cubic-bezier(.22,1,.36,1) 0.15s;
          padding: 0 3px;
          border-radius: 3px;
          -webkit-box-decoration-break: clone;
          box-decoration-break: clone;
        }
        .hl.hl-active { background-size: 100% 88%; }
        .hl-yellow { background-image: linear-gradient(120deg, #fef08a 100%, transparent 100%); }
        .hl-blue   { background-image: linear-gradient(120deg, #bfdbfe 100%, transparent 100%); }
        .hl-pink   { background-image: linear-gradient(120deg, #fbcfe8 100%, transparent 100%); }

        /* ── Pull quote ── */
        .cs-pull-quote {
          font-size: clamp(1.5rem, 3.5vw, 2.3rem);
          font-weight: 700;
          line-height: 1.25;
          letter-spacing: -0.025em;
          color: #1d1d1f;
          text-align: center;
          padding: 3.5rem 0;
          border-top: 1px solid #e5e5e5;
          border-bottom: 1px solid #e5e5e5;
          margin: 2.5rem 0;
        }
        .cs-pull-quote em { color: #0071e3; font-style: normal; }

        /* ── Act number watermark ── */
        .cs-act-num {
          font-size: clamp(6rem, 15vw, 12rem);
          font-weight: 800;
          line-height: 1;
          letter-spacing: -0.06em;
          color: rgba(255,255,255,0.05);
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          user-select: none;
        }

        /* ── Cards ── */
        .cs-stat-card { transition: transform 0.25s ease, box-shadow 0.25s ease; cursor: default; }
        .cs-stat-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(0,113,227,0.12); }
        .cs-quote-card { transition: box-shadow 0.25s ease, transform 0.25s ease; }
        .cs-quote-card:hover { box-shadow: 0 6px 28px rgba(0,0,0,0.1); transform: translateY(-2px); }
        .cs-step:hover { background: #eef4ff !important; }
        .cs-step { transition: background 0.2s; }
      `}</style>

      <IdentityCard />

      {/* ════════════════════ HERO ════════════════════ */}
      <section style={{ padding: '7.5rem 0 3.5rem', background: '#fff' }}>
        <div className="container">
          <div>
            <div className="cs-reveal" style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1.3rem' }}>
              <span style={badge('#e0edff', '#0052cc')}>ASSETS 2025 · Denver, CO</span>
              <span style={badge('#fce7f3', '#9d174d')}>CHI 2026 · Barcelona, Spain</span>
              <span style={badge('#f0fdf4', '#166534')}>Open Source</span>
            </div>
            <h1 className="cs-reveal d1" style={{ fontSize: 'clamp(2rem, 5vw, 3.1rem)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.04em', marginBottom: '1.1rem', color: '#1d1d1f' }}>
              DIXTRAL: Making 3D Scientific Data Accessible to Blind and Low-Vision Researchers
            </h1>
            <p className="cs-reveal d2" style={{ fontSize: '1.12rem', color: '#555', lineHeight: 1.75, marginBottom: '1.5rem' }}>
              I spent months building 3D data visualization tools, then realized the only person on the team who could actually use them was me. This is what I built to fix that — <Hl color="blue">published at two premier HCI venues</Hl> and open-sourced for any BLV researcher to use today.
            </p>
            <div className="cs-reveal d3" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
              {['Accessibility', 'Co-Design', 'WebGL', 'Sonification', 'HCI', 'Mixed Methods', 'Lead Developer + Researcher'].map(t => (
                <span key={t} className="tag">{t}</span>
              ))}
            </div>
            <div className="cs-reveal d4" style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              <a href="https://sanchitakamath.com/dixtral-assets2025-demo/" target="_blank" rel="noopener noreferrer"
                style={{ background: '#0071e3', color: '#fff', fontWeight: 700, padding: '0.7rem 1.6rem', borderRadius: 8, textDecoration: 'none', fontSize: '0.95rem' }}>
                Try the Live Demo &rarr;
              </a>
              <a href="https://doi.org/10.1145/3663547.3759765" target="_blank" rel="noopener noreferrer" style={btnOutline}>ASSETS Paper</a>
              <a href="https://doi.org/10.1145/3772318.3791272" target="_blank" rel="noopener noreferrer" style={btnOutline}>CHI Paper</a>
              <a href="https://github.com/SK-143381/dixtral-assets2025-demo" target="_blank" rel="noopener noreferrer" style={btnOutlineGray}>GitHub</a>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ STATS ════════════════════ */}
      <section style={{ background: '#f5f5f7', padding: '3rem 0' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))', gap: '1.1rem' }}>
            {[
              { target: 2, suffix: '', label: 'Premier Venues', sub: 'ASSETS + CHI' },
              { target: 5, suffix: '', label: 'BLV Co-Designers', sub: 'Across both studies' },
              { target: 17, suffix: '', label: 'Keyboard Shortcuts', sub: '10 base + 7 added at CHI' },
              { target: 3, suffix: '', label: 'Feedback Channels', sub: 'Sound · TTS · Visual' },
              { target: 0, suffix: '', label: 'Screen Reader Conflicts', sub: 'NVDA, JAWS, VoiceOver' },
            ].map((s, i) => (
              <div key={s.label} className={`cs-reveal cs-stat-card d${i + 1}`}
                style={{ background: '#fff', borderRadius: 14, padding: '1.4rem 1.2rem', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <CountStat target={s.target} suffix={s.suffix} />
                <div style={{ fontWeight: 600, fontSize: '0.87rem', marginTop: '0.4rem', color: '#1d1d1f' }}>{s.label}</div>
                <div style={{ fontSize: '0.74rem', color: '#999', marginTop: '0.15rem' }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ COVER IMAGE (fades on scroll) ════════════════════ */}
      <section ref={coverRef} style={{ background: '#0a0a0a', padding: '3.5rem 0', position: 'relative' }}>
        <div className="container" style={{ opacity: coverOpacity, transition: 'opacity 0.1s linear' }}>
          <div className="cs-reveal">
            <img
              src="/img/dixtral/dixtral.png"
              alt="The DIXTRAL interface: 3D surface plot on a dark background, white highlighted rectangle marking the keyboard cursor, statistics panel on the right."
              style={{ width: '100%', borderRadius: 14, display: 'block', boxShadow: '0 0 60px rgba(0,113,227,0.15)' }}
            />
            <p style={{ textAlign: 'center', color: '#666', fontSize: '0.82rem', marginTop: '1rem' }}>
              That white rectangle is the cursor. It moves with the keyboard, plays a tone, and reads itself aloud. No mouse required.
            </p>
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.8rem' }}>
            <a href="https://sanchitakamath.com/dixtral-assets2025-demo/" target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', background: '#0071e3', color: '#fff', fontWeight: 700, padding: '0.8rem 2rem', borderRadius: 8, textDecoration: 'none', fontSize: '1rem' }}>
              Try it yourself &rarr;
            </a>
            <p style={{ color: '#555', fontSize: '0.8rem', marginTop: '0.5rem' }}>Works in any browser. Bring headphones for the stereo audio.</p>
          </div>
        </div>
      </section>

      {/* ════════════════════ MY ROLE ════════════════════ */}
      <section style={secWhite}>
        <div className="container">
          <span className="cs-reveal" style={pill}>My Contribution</span>
          <h2 className="cs-reveal" style={h2}>What I did on this project</h2>
          <p className="cs-reveal" style={prose}>
            I was the <Hl color="yellow">lead developer, designer, and lead researcher</Hl> across both papers. I built the entire system — the WebGL rendering engine, the Web Audio API sonification pipeline, keyboard navigation, screen reader integration, and the five-layer modular architecture. I designed every interaction pattern, all audio mappings, the text verbosity system, and the physical tactile prototype used in the CHI study. I organized every co-design session, wrote both sets of research questions, and led both manuscripts to publication.
          </p>
          <p className="cs-reveal" style={{ ...prose, fontStyle: 'italic', color: '#888' }}>
            I was, technically, the only person on the team who could see the 3D plots I spent months building. Therefore interaction Mechanisms were paramount!
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            {[
              { icon: '💻', t: 'Lead Developer', d: 'Built everything from scratch: WebGL engine, Web Audio API sonification, keyboard navigation, ARIA sync, five-layer event-driven architecture.' },
              { icon: '🎨', t: 'Sole Designer', d: 'Designed every interaction: audio mappings, verbosity levels, highlighting, keybindings, dark mode, and a physical tactile probe with braille labels.' },
              { icon: '🔬', t: 'Lead Researcher', d: 'Wrote both papers, formulated both research questions, ran all co-design sessions, performed all analysis, led both manuscripts to acceptance.' },
              { icon: '🤝', t: 'Co-Design Partner', d: 'Daily sessions with Aziz throughout. Weekly sessions with JooYoung. Facilitated two formal EBCD sessions with the full six-person expert team.' },
            ].map((r, i) => (
              <div key={r.t} className={`cs-reveal d${i + 1}`}
                style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.4rem', border: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: '1.7rem', marginBottom: '0.5rem' }}>{r.icon}</div>
                <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.95rem' }}>{r.t}</div>
                <p style={{ fontSize: '0.86rem', color: '#666', margin: 0, lineHeight: 1.6 }}>{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ BACKGROUND ════════════════════ */}
      <section style={secGray}>
        <div className="container">
          <span className="cs-reveal" style={pill}>Background</span>
          <h2 className="cs-reveal" style={h2}>What is a 3D surface plot, and why does it matter?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2.5rem', alignItems: 'start' }}>
            <div>
              <p className="cs-reveal" style={prose}>
                A surface plot shows <Hl color="blue">three variables at once</Hl>. Two spread across a flat grid; the third rises as height above it. Think of it as a landscape: mountains are high values, valleys are low, slopes show rate of change. Scientists use these constantly — meteorologists, biomedical researchers, spectroscopists.
              </p>
              <p className="cs-reveal" style={prose}>
                For a sighted user this is instant. Your eye reads the whole landscape in one glance. For a BLV user with a screen reader, the landscape goes completely silent. WebGL renders to a canvas element. Canvas elements are, by default, invisible to assistive technology. There is nothing to read.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                { head: 'Alt text tells you what a chart looks like, not what the data means', body: '"A 3D surface plot with blue and red regions" does nothing for scientific analysis. You need peaks, slopes, global trends.' },
                { head: 'Tactile models work, but do not scale', body: 'A raised physical model lets you feel the data — it also takes a day to produce, cannot update when the data changes, and cannot be emailed.' },
                { head: 'No browser-native accessible tool existed', body: 'Before DIXTRAL, to the best of our knowledge, no web-native system offered interactive accessible 3D scalar field visualization.' },
              ].map((c, i) => (
                <div key={c.head} className={`cs-reveal d${i + 1}`}
                  style={{ background: '#fff', borderRadius: 12, padding: '1.1rem 1.3rem', borderLeft: '3px solid #0071e3' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1d1d1f', marginBottom: '0.35rem' }}>{c.head}</div>
                  <p style={{ fontSize: '0.84rem', color: '#666', margin: 0, lineHeight: 1.58 }}>{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ ACT 1 DIVIDER ════════════════════ */}
      <div style={{ position: 'relative', background: '#0a0a0a', padding: '5rem 0', overflow: 'hidden', textAlign: 'center' }}>
        <div className="cs-act-num">01</div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="cs-reveal" style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', marginBottom: '0.7rem' }}>ACT ONE</div>
          <h2 className="cs-reveal" style={{ color: '#fff', fontSize: 'clamp(1.7rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, maxWidth: 560, margin: '0 auto 0.8rem' }}>
            The call that rewired how I think about design
          </h2>
          <p className="cs-reveal" style={{ color: '#777', maxWidth: 400, margin: '0 auto', fontSize: '1rem' }}>One sentence. The entire project.</p>
        </div>
      </div>

      {/* ════════════════════ ACT 1 CONTENT ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="cs-reveal" style={prose}>
            I was building 3D surface plots for scientific data. They are genuinely useful and genuinely beautiful. They also <Hl color="yellow">completely assumed you could see them</Hl>. Every tool I used treated the mouse and the eyeball as the only valid input. Screen readers hit a wall of WebGL and went silent.
          </p>
          <p className="cs-reveal" style={prose}>
            Then I got on a video call with JooYoung, an Assistant Professor who has been navigating the world through a screen reader since he was 11.
          </p>

          <blockquote className="cs-reveal cs-pull-quote">
            "I would have to use a physical prototype to understand the data first.{' '}
            <em>I wouldn't be able to visualize it through an interface.</em>"
            <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#999', marginTop: '1rem', letterSpacing: 0 }}>JooYoung Seo, during our first video call</div>
          </blockquote>

          <p className="cs-reveal" style={prose}>
            Here was a researcher with deep spatial intelligence, describing his own data by asking a sighted colleague to read it back to him. Not because he could not understand the data. Because <Hl color="blue">every tool assumed he could not show up</Hl>.
          </p>
          <p className="cs-reveal" style={prose}>Aziz, a graduate researcher and my closest collaborator throughout both studies, put the same problem more directly:</p>

          <blockquote className="cs-reveal cs-pull-quote">
            "I have to ask sighted colleagues to describe what they see, but{' '}
            <em>they miss the patterns I'd notice if I could explore myself.</em>"
            <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#999', marginTop: '1rem', letterSpacing: 0 }}>Aziz Zeidieh, co-design session</div>
          </blockquote>

          <p className="cs-reveal" style={prose}>
            That is not an inconvenience. That is a colleague being cut out of their own research process because nobody thought to build a door.
          </p>
          <p className="cs-reveal" style={{ ...prose, fontWeight: 700, color: '#1d1d1f', fontSize: '1.08rem' }}>
            I stopped asking "how do I make this visual tool accessible?" and started asking "<Hl color="yellow">how do I build data exploration that works without sight as the primary input?</Hl>" That shift is the entire project.
          </p>
        </div>
      </section>

      {/* ════════════════════ ACT 2 DIVIDER ════════════════════ */}
      <div style={{ position: 'relative', background: '#0a0a0a', padding: '5rem 0', overflow: 'hidden', textAlign: 'center' }}>
        <div className="cs-act-num">02</div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="cs-reveal" style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', marginBottom: '0.7rem' }}>ACT TWO</div>
          <h2 className="cs-reveal" style={{ color: '#fff', fontSize: 'clamp(1.7rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, maxWidth: 680, margin: '0 auto 0.8rem' }}>
            Building DIXTRAL
          </h2>
          <p className="cs-reveal" style={{ color: '#777', maxWidth: 560, margin: '0 auto 2rem', fontSize: '1rem' }}>
            Dynamic Interface eXploration of Three-dimensional Representations for Adaptive Learning.{' '}
            Started as "Project Audiograf." Grew into two publications.
          </p>
          <div className="cs-reveal" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {['Decision: which interaction model?', 'Problem 01: space without sight', 'Problem 02: sound that confuses', 'Problem 03: architecture crisis'].map(p => (
              <div key={p} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '0.5rem 1rem', color: '#ccc', fontSize: '0.82rem', fontWeight: 500 }}>{p}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════ DESIGN DECISION: INTERACTION MODEL ════════════════════ */}
      <section style={secWhite}>
        <div className="container">
          <span className="cs-reveal" style={pill}>Before a single line of code</span>
          <h2 className="cs-reveal" style={h2}>Two interaction paradigms. One clear winner.</h2>
          <p className="cs-reveal" style={prose}>
            Before I wrote anything, I mapped out two fundamentally different approaches to non-visual 3D navigation. The question was not just "what keys do we use" — it was <Hl color="blue">what mental model does the user have to hold in their head</Hl> while exploring.
          </p>
          <div className="cs-reveal" style={{ margin: '1.5rem 0 0.5rem' }}>
            <img
              src="/img/dixtral/deciding-interaction.png"
              alt="Diagram comparing two interaction paradigms: A) Free-roaming cursor leading to continuous mouse movement, high cognitive load, and dynamic pitch shift that is difficult to parse; B) Grid-based stepping leading to discrete keypress navigation, low cognitive load, and quantized tones that are easier to pattern."
              style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            <div className="cs-reveal d1" style={{ background: '#fff5f5', borderRadius: 12, padding: '1.2rem', border: '1px solid #fecaca' }}>
              <div style={{ fontWeight: 800, color: '#dc2626', marginBottom: '0.4rem', fontSize: '0.88rem' }}>Option A: Free-roaming cursor</div>
              <p style={{ fontSize: '0.86rem', color: '#555', margin: 0, lineHeight: 1.6 }}>
                Continuous mouse movement across a conceptual data space. Sounds good in theory. In practice: <strong>where are the boundaries?</strong> The pitch shifts continuously, which means the brain has to track relative movement, absolute position, and data value simultaneously. High cognitive load. Hard to parse.
              </p>
            </div>
            <div className="cs-reveal d2" style={{ background: '#f0fdf4', borderRadius: 12, padding: '1.2rem', border: '1px solid #86efac' }}>
              <div style={{ fontWeight: 800, color: '#16a34a', marginBottom: '0.4rem', fontSize: '0.88rem' }}>Option B: Grid-based stepping — chosen</div>
              <p style={{ fontSize: '0.86rem', color: '#555', margin: 0, lineHeight: 1.6 }}>
                A pre-defined grid structure. Discrete keypresses. <strong>Quantized tones at each grid position.</strong> Each step is a single sound at a specific pitch. The brain gets one clear signal per action. Low cognitive load. Patterns emerge naturally. This became the entire navigation model.
              </p>
            </div>
          </div>
          <p className="cs-reveal" style={{ ...prose, marginTop: '1.2rem', color: '#888', fontStyle: 'italic' }}>
            The diagram above also flagged "consider non-visual feedback modalities (audio/tactile)" — this annotation became the blueprint for the entire sonification system that followed.
          </p>
        </div>
      </section>

      {/* ════════════════════ PROBLEM 1 ════════════════════ */}
      <section style={secGray}>
        <div className="container" style={{ maxWidth: 780 }}>
          <div className="cs-reveal" style={problemBadge}>Problem 01</div>
          <h2 className="cs-reveal" style={h2}>Space without sight</h2>
          <p className="cs-reveal" style={prose}>By week 2, I thought I had navigation figured out. Arrow keys moved point by point. Coordinates were announced. Seemed reasonable.</p>
          <p className="cs-reveal" style={prose}>Then JooYoung tested the first prototype.</p>

          <blockquote className="cs-reveal cs-pull-quote">
            "<em>How do I know where I am in 3D space</em> without seeing it?"
            <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#999', marginTop: '1rem', letterSpacing: 0 }}>JooYoung, week 2</div>
          </blockquote>

          <p className="cs-reveal" style={prose}>I had built navigation. I had not built orientation. Sighted users maintain a mental map passively — by looking. My system gave no equivalent. Five days later, Aziz said the same thing from a different angle:</p>

          <blockquote className="cs-reveal cs-pull-quote">
            "I keep getting lost in the data. <em>There are no landmarks.</em>"
            <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#999', marginTop: '1rem', letterSpacing: 0 }}>Aziz, week 2</div>
          </blockquote>

          <p className="cs-reveal" style={prose}>
            Watching Aziz navigate, I noticed he was not moving randomly — he had spatial strategies: sweep an axis, jump to specific regions. My system could support neither. I had designed exactly one way to move through the data. Mine.
          </p>

          <div className="cs-reveal" style={lightbulbBox}>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>💡</div>
            <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '1rem' }}>The realization</div>
            <p style={{ margin: 0, lineHeight: 1.65, color: '#444' }}>
              JooYoung thought in systematic axis sweeps. Aziz preferred jumping to specific regions. Both are valid spatial reasoning strategies. My system was the limitation, not their cognition. I needed to support multiple approaches, not enforce one.
            </p>
          </div>

          <p className="cs-reveal" style={prose}>
            That weekend I rewrote navigation from scratch. The N key cycles three independent axes: Y, Z, X. Arrow keys move within the active axis. Ctrl+Shift+Arrow jumps between segments. <Hl color="yellow">Switching axes preserves your exact position</Hl> — you never lose your place.
          </p>

          <div className="cs-reveal" style={codeBlock}>
            <div style={{ color: '#86868b', marginBottom: '0.4rem', fontSize: '0.76rem' }}>NavigationController.js — tri-axis with position preservation</div>
            <div><span style={{ color: '#60a5fa' }}>toggleNavigationAxis</span>{'() {'}</div>
            <div style={{ paddingLeft: '1.2rem' }}>{'this.currentAxis = (this.currentAxis + '}<span style={{ color: '#f59e0b' }}>1</span>{') % '}<span style={{ color: '#f59e0b' }}>3</span>{'  '}<span style={{ color: '#6b7280', fontStyle: 'italic' }}>{'// Y → Z → X → Y'}</span></div>
            <div style={{ paddingLeft: '1.2rem' }}>{'this.'}<span style={{ color: '#60a5fa' }}>preservePosition</span>{'()'}</div>
            <div style={{ paddingLeft: '1.2rem' }}>{'this.'}<span style={{ color: '#60a5fa' }}>announceAxisChange</span>{'()'}</div>
            {'}'}
          </div>

          <div className="cs-reveal" style={{ ...lightbulbBox, background: '#f0fdf4', border: '1.5px solid #16a34a' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#166534', marginBottom: '0.5rem' }}>Result — week 4</div>
            <p style={{ fontSize: '1.08rem', fontStyle: 'italic', color: '#1d1d1f', margin: 0, lineHeight: 1.6 }}>
              "I can finally think in the data instead of fighting the interface."
            </p>
            <div style={{ fontSize: '0.82rem', color: '#555', marginTop: '0.4rem', fontWeight: 600 }}>JooYoung</div>
          </div>
        </div>
      </section>

      {/* ════════════════════ PROBLEM 2 ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 780 }}>
          <div className="cs-reveal" style={problemBadge}>Problem 02</div>
          <h2 className="cs-reveal" style={h2}>Sound that confused instead of clarified</h2>
          <p className="cs-reveal" style={prose}>My first sonification mapped data values to volume. High value, loud sound. Seemed intuitive — to me.</p>
          <p className="cs-reveal" style={prose}>Day 12, Aziz stopped testing:</p>

          <blockquote className="cs-reveal cs-pull-quote">
            "I can't distinguish between high Y-values and{' '}
            <em>high volume levels. They sound the same.</em>"
            <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#999', marginTop: '1rem', letterSpacing: 0 }}>Aziz, day 12</div>
          </blockquote>

          <p className="cs-reveal" style={prose}>"Loud" meant both "this data point is high" and "the audio is loud." Two different things. One ambiguous signal. JooYoung sharpened the diagnosis:</p>

          <blockquote className="cs-reveal cs-pull-quote">
            "<em>The audio needs to tell me about the data,</em> not just make noise."
            <div style={{ fontSize: '0.88rem', fontWeight: 500, color: '#999', marginTop: '1rem', letterSpacing: 0 }}>JooYoung, week 2</div>
          </blockquote>

          <div className="cs-reveal" style={lightbulbBox}>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.6rem' }}>💡</div>
            <div style={{ fontWeight: 700, marginBottom: '0.8rem', fontSize: '1rem' }}>The audio language I built — <Hl color="yellow">four orthogonal channels</Hl></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))', gap: '0.7rem' }}>
              {[
                { ch: 'Pitch', map: 'Y-axis value', note: '200–1200 Hz. One unambiguous signal per data height.' },
                { ch: 'Timbre', map: 'X-axis position', note: 'Sine, triangle, square waveforms as you move left to right.' },
                { ch: 'Duration', map: 'Z-axis depth', note: 'Longer note = deeper in the grid. Distinct from pitch.' },
                { ch: 'Boundary', map: 'Dataset edges', note: 'Distinct audio at borders — you always know when you hit the wall.' },
              ].map(a => (
                <div key={a.ch} style={{ background: '#fff', borderRadius: 10, padding: '0.9rem' }}>
                  <div style={{ fontWeight: 800, color: '#0071e3', fontSize: '1rem' }}>{a.ch}</div>
                  <div style={{ fontSize: '0.78rem', color: '#444', margin: '0.2rem 0 0.1rem', fontWeight: 600 }}>{a.map}</div>
                  <div style={{ fontSize: '0.74rem', color: '#888', lineHeight: 1.4 }}>{a.note}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="cs-reveal" style={{ ...lightbulbBox, background: '#f0fdf4', border: '1.5px solid #16a34a' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#166534', marginBottom: '0.5rem' }}>Result — day 18</div>
            <p style={{ fontSize: '1.08rem', fontStyle: 'italic', color: '#1d1d1f', margin: 0, lineHeight: 1.6 }}>
              "I can hear the patterns! High peaks sound different from loud sounds. I finally understand what you meant."
            </p>
            <div style={{ fontSize: '0.82rem', color: '#555', marginTop: '0.4rem', fontWeight: 600 }}>Aziz</div>
          </div>
        </div>
      </section>

      {/* ════════════════════ PROBLEM 3 ════════════════════ */}
      <section style={secGray}>
        <div className="container" style={{ maxWidth: 780 }}>
          <div className="cs-reveal" style={problemBadge}>Problem 03</div>
          <h2 className="cs-reveal" style={h2}>The architecture emergency</h2>
          <p className="cs-reveal" style={prose}>Week 3. Not a research crisis. A code crisis.</p>
          <p className="cs-reveal" style={prose}>
            Every time JooYoung or Aziz asked for a change, I rewrote code across three files. The sonification system was tangled with navigation logic, which was tangled with the WebGL engine. <Hl color="pink">Changing audio broke keyboard navigation. Adding a display mode crashed the statistics panel.</Hl>
          </p>

          <div className="cs-reveal" style={lightbulbBox}>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>💡</div>
            <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '1rem' }}>The fix: five strictly-separated layers</div>
            <p style={{ margin: '0 0 1rem', color: '#555', fontSize: '0.9rem', lineHeight: 1.65 }}>
              Each layer knows only about itself and the layers below it. All cross-layer communication goes through a central event bus. Global variables: banned. Direct cross-layer imports: banned.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {[
                { name: 'Application Layer', file: 'app.js', note: 'Global coordinator. Initializes everything. Owns nothing.', accent: false },
                { name: 'UI Layer', file: 'UIController.js', note: 'Menus, dark mode, axis labels. Purely visual.', accent: false },
                { name: 'Accessibility Layer', file: 'NavigationController.js', note: 'ALL non-visual logic: keyboard, gamepad, sonification, TTS, ARIA.', accent: true },
                { name: 'Engine Layer', file: 'VisualizationEngine.js', note: 'Custom WebGL rendering, GLSL shaders, highlight buffers.', accent: false },
                { name: 'Data Layer', file: 'PlotData.js', note: 'Data ingestion, normalization, statistics. No display logic whatsoever.', accent: false },
              ].map(l => (
                <div key={l.name} style={{ background: l.accent ? '#eef4ff' : '#f9f9fb', borderRadius: 8, padding: '0.6rem 1rem', display: 'flex', gap: '0.8rem', alignItems: 'flex-start', borderLeft: l.accent ? '3px solid #0071e3' : '3px solid transparent' }}>
                  <div style={{ minWidth: 155 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: l.accent ? '#0071e3' : '#1d1d1f' }}>{l.name}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.74rem', color: '#888' }}>{l.file}</div>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.5 }}>{l.note}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="cs-reveal" style={{ ...lightbulbBox, background: '#f0fdf4', border: '1.5px solid #16a34a' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#166534', marginBottom: '0.5rem' }}>Result</div>
            <p style={{ margin: 0, color: '#444', lineHeight: 1.65 }}>
              JooYoung asked for a review mode — a scrollable text log of every explored point, readable with any screen reader.<br />
              <strong>Before the refactor: 3 hours across 6 files. After: 30 minutes in 1 controller.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════ INTERACTION LOGIC DIAGRAM ════════════════════ */}
      <section style={secWhite}>
        <div className="container">
          <span className="cs-reveal" style={pill}>System Architecture</span>
          <h2 className="cs-reveal" style={h2}>How a single keypress becomes a non-visual experience</h2>
          <p className="cs-reveal" style={prose}>
            Every user action — a keypress, a gamepad joystick, even a hover — triggers a deterministic chain through the DIXTRAL engine. The chain always ends in at least two simultaneous non-visual outputs: a tone and spoken text. They fire together, never competing.
          </p>
          <div className="cs-reveal" style={{ margin: '1.5rem 0 0.5rem' }}>
            <img
              src="/img/dixtral/interaction-logic.png"
              alt="Flowchart showing user actions (arrow keys, Ctrl+Shift+Up, hover) flowing into DIXTRAL engine processing (axis increment, Y-value calculation, boundary collision check, telemetry log, pitch mapping) and outputting to sonification tones, TTS speech, and future haptic feedback."
              style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}
            />
          </div>
          <p className="cs-reveal" style={{ ...prose, color: '#888', fontStyle: 'italic', marginTop: '0.8rem' }}>
            The key research direction in the CHI paper; grounded by expert BLV researchers in Session 2, was the introduction of haptics (Case Study coming soon).
          </p>
        </div>
      </section>

      {/* ════════════════════ PROTOTYPE EVOLUTION ════════════════════ */}
      <section style={secGray}>
        <div className="container">
          <span className="cs-reveal" style={pill}>UI Evolution</span>
          <h2 className="cs-reveal" style={h2}>From Project Audiograf to DIXTRAL</h2>
          <p className="cs-reveal" style={prose}>
            The three versions below were not sequential redesigns — they were <Hl color="blue">responses to specific failures</Hl> that emerged from testing with JooYoung and Aziz. Each version removed something.
          </p>
          <div className="cs-reveal" style={{ margin: '1.5rem 0 0.5rem' }}>
            <img
              src="/img/dixtral/prototypes.png"
              alt="Three prototype stages: first version (Project Audiograf v0.1) with a heavy right-side panel showing sonification pattern radio buttons and a TTS feedback log; second version with a separate accessibility settings panel with sliders for TTS verbosity and sonification density; final DIXTRAL interface with the side panel removed entirely, replaced by compact icon buttons on the left edge and a full-screen visualization with a live accessibility text console at the bottom."
              style={{ width: '100%', borderRadius: 12, boxShadow: '0 2px 20px rgba(0,0,0,0.08)', border: '1px solid #eee' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            {[
              { v: 'v0.1 — first build', color: '#6b7280', t: 'Dense side panel', b: 'Sonification pattern radio buttons, TTS feedback log, navigation controls — all visible at once. Looked complete. Was exhausting. The side panel added visual and cognitive clutter, and for a screen reader user, a large panel means constant focus traversal to reach the main content.' },
              { v: 'v0.1 — second pass', color: '#f59e0b', t: 'Panel becomes modal', b: 'The accessibility settings moved into a separate panel with sliders for TTS verbosity and sonification density. Better than always-visible. But the focus still had to move out of the visualization area to change any setting — a constant interruption to the exploration flow.' },
              { v: 'DIXTRAL — final', color: '#16a34a', t: 'Side panel removed entirely', b: 'All settings became keyboard shortcuts. The side panel disappeared. A compact icon-button edge replaced it. The live accessibility text console moved to the bottom as a single-line strip. Result: the visualization fills the screen, focus never leaves the data, and screen reader navigation becomes linear and predictable.' },
            ].map((p, i) => (
              <div key={p.v} className={`cs-reveal d${i + 1}`} style={{ background: '#fff', borderRadius: 12, padding: '1.2rem', borderTop: `3px solid ${p.color}` }}>
                <div style={{ fontWeight: 800, fontSize: '0.78rem', color: p.color, marginBottom: '0.3rem', letterSpacing: '0.05em' }}>{p.v}</div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>{p.t}</div>
                <p style={{ fontSize: '0.84rem', color: '#555', margin: 0, lineHeight: 1.55 }}>{p.b}</p>
              </div>
            ))}
          </div>
          <div className="cs-reveal" style={{ ...lightbulbBox, marginTop: '1.8rem' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>💡</div>
            <p style={{ margin: 0, color: '#444', lineHeight: 1.7, fontSize: '0.95rem' }}>
              The decision to remove the side panel was driven by one specific screen reader problem: <strong>focus management</strong>. Every time a user tabbed into the side panel to change a setting, the virtual cursor moved away from the visualization. Getting back required re-navigating through the DOM. For a BLV user doing intensive data exploration, this happened constantly. Removing the panel and making every setting a keystroke meant <Hl color="yellow">the focus never left the data</Hl>.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════ GIFs ════════════════════ */}
      <section style={{ background: '#0a0a0a', padding: '4rem 0' }}>
        <div className="container">
          <h2 className="cs-reveal" style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(1.5rem, 3vw, 2rem)', textAlign: 'center', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>See it in action</h2>
          <p className="cs-reveal" style={{ color: '#666', textAlign: 'center', marginBottom: '2.5rem', fontSize: '0.92rem' }}>Two display modes. Both fully keyboard-navigable, screen-reader compatible, and sonified.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
            {[
              { src: '/img/dixtral/surface-mode.gif', alt: 'Surface mode: 3D mesh with a white highlighted rectangle sweeping the surface as the user navigates by keyboard.', label: 'Surface Mode (press 1)', desc: 'One wireframe cell at a time. Each position plays a tone and announces its coordinates and value.' },
              { src: '/img/dixtral/point-mode.gif', alt: 'Point mode: individual data points with a magenta enlarged circle highlighting the focused point as the user navigates.', label: 'Point Mode (press 2)', desc: 'Pitch = Y value. Stereo pan = X position. Tone duration = Z depth. Three simultaneous data channels.' },
            ].map((g, i) => (
              <div key={g.label} className={`cs-reveal d${i + 1}`} style={{ background: '#141414', borderRadius: 14, overflow: 'hidden', border: '1px solid #222' }}>
                <ReplayGif src={g.src} alt={g.alt} style={{ width: '100%', display: 'block' }} />
                <div style={{ padding: '1rem 1.1rem', borderTop: '1px solid #222' }}>
                  <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.92rem', marginBottom: '0.3rem' }}>{g.label}</div>
                  <p style={{ color: '#888', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>{g.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="cs-reveal" style={{ textAlign: 'center' }}>
            <a href="https://sanchitakamath.com/dixtral-assets2025-demo/" target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', background: '#0071e3', color: '#fff', fontWeight: 700, padding: '0.85rem 2.2rem', borderRadius: 8, textDecoration: 'none', fontSize: '1rem' }}>
              Try it yourself &rarr;
            </a>
          </div>
        </div>
      </section>

      {/* ════════════════════ PAPER 1: ASSETS ════════════════════ */}
      <section style={secWhite}>
        <div className="container">
          <div className="cs-reveal" style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <span style={badge('#e0edff', '#0052cc')}>Paper 1</span>
            <span style={badge('#e0edff', '#0052cc')}>ASSETS 2025 · Denver, CO</span>
          </div>
          <h2 className="cs-reveal" style={h2}>What the first paper showed</h2>
          <p className="cs-reveal" style={{ color: '#888', fontStyle: 'italic', marginBottom: '1.5rem', fontSize: '0.92rem' }}>
            "Explore, Listen, Inspect: Supporting Multimodal Interaction with 3D Surface and Point Data Visualizations"
          </p>
          <p className="cs-reveal" style={prose}>
            The paper asked three questions about the system I had built. All three came back affirmative — but the more interesting answers were in the <Hl color="blue">how and why</Hl>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', margin: '1.5rem 0' }}>
            {[
              { rq: 'RQ1', q: 'Can multimodal interaction make a 3D surface plot accessible?', finding: 'Yes. JooYoung described a plot\'s structure by listening alone. Aziz used sonification and visual highlight together. Three channels worked in sync with zero screen reader conflicts across NVDA, JAWS, and VoiceOver.' },
              { rq: 'RQ2', q: 'Can users get both high-level overview and point-level detail?', finding: 'Autoplay (P key) sweeps at 8 pts/sec. Before it existed, both JooYoung and Aziz found it nearly impossible to form a mental model of the full surface. With autoplay, Aziz identified a dataset\'s overall trend independently for the first time.' },
              { rq: 'RQ3', q: 'Can accessibility and visual experience coexist without tradeoffs?', finding: 'Yes. The accessibility layer is completely independent of the WebGL engine. Aziz — who has some usable vision — used screen reader output and the visual highlight simultaneously with no degradation to either channel.' },
            ].map((r, i) => (
              <div key={r.rq} className={`cs-reveal d${i + 1}`} style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.3rem', borderTop: '3px solid #0071e3' }}>
                <div style={{ fontWeight: 800, fontSize: '0.78rem', color: '#0071e3', marginBottom: '0.3rem', letterSpacing: '0.06em' }}>{r.rq}</div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1d1d1f', marginBottom: '0.5rem' }}>{r.q}</div>
                <p style={{ fontSize: '0.86rem', color: '#555', margin: 0, lineHeight: 1.58 }}>{r.finding}</p>
              </div>
            ))}
          </div>

          <div className="cs-reveal" style={{ ...lightbulbBox, background: '#f0fdf4', border: '1.5px solid #16a34a' }}>
            <p style={{ fontSize: '1.12rem', fontStyle: 'italic', color: '#1d1d1f', lineHeight: 1.6, margin: '0 0 0.5rem' }}>
              "For the first time, I'm not asking permission to explore data. <strong>I'm just exploring.</strong>"
            </p>
            <div style={{ fontSize: '0.82rem', color: '#555', fontWeight: 600 }}>Aziz, during co-design testing</div>
          </div>

          <div className="cs-reveal" style={{ marginTop: '1.8rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <a href="https://doi.org/10.1145/3663547.3759765" target="_blank" rel="noopener noreferrer" style={btnOutline}>Read the ASSETS Paper &rarr;</a>
            <a href="https://github.com/SK-143381/dixtral-assets2025-demo" target="_blank" rel="noopener noreferrer" style={btnOutlineGray}>GitHub</a>
          </div>
        </div>
      </section>

      {/* ════════════════════ ACT 3 DIVIDER ════════════════════ */}
      <div style={{ position: 'relative', background: '#0a0a0a', padding: '5rem 0', overflow: 'hidden', textAlign: 'center' }}>
        <div className="cs-act-num">03</div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="cs-reveal" style={{ color: '#0071e3', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', marginBottom: '0.7rem' }}>ACT THREE</div>
          <h2 className="cs-reveal" style={{ color: '#fff', fontSize: 'clamp(1.7rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, maxWidth: 600, margin: '0 auto 0.8rem' }}>
            Then it got bigger
          </h2>
          <p className="cs-reveal" style={{ color: '#777', maxWidth: 500, margin: '0 auto 2.5rem', fontSize: '1rem' }}>
            ASSETS showed the tool worked for two people. The next question: does it hold up when five expert BLV researchers engage with it at once?
          </p>
          <div className="cs-reveal" style={{ display: 'flex', justifyContent: 'center', gap: '4rem', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#0071e3', fontSize: '3.5rem', fontWeight: 800 }}>2</div>
              <div style={{ color: '#888', fontSize: '0.85rem' }}>researchers — ASSETS phase</div>
            </div>
            <div style={{ color: '#333', fontSize: '2rem', alignSelf: 'center' }}>&#8594;</div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#0071e3', fontSize: '3.5rem', fontWeight: 800 }}>6</div>
              <div style={{ color: '#888', fontSize: '0.85rem' }}>researchers — CHI (5 BLV)</div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════ PAPER 2: CHI ════════════════════ */}
      <section style={secWhite}>
        <div className="container">
          <div className="cs-reveal" style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <span style={badge('#fce7f3', '#9d174d')}>Paper 2</span>
            <span style={badge('#fce7f3', '#9d174d')}>CHI 2026 · Barcelona, Spain</span>
          </div>
          <h2 className="cs-reveal" style={h2}>Three modalities, two probes, one prototype, no vision</h2>
          <p className="cs-reveal" style={{ color: '#888', fontStyle: 'italic', marginBottom: '1.5rem', fontSize: '0.92rem' }}>
            "Experience-Based Co-Design of a Multi-modal 3D Data Visualization Tool"
          </p>
          <p className="cs-reveal" style={prose}>
            I used Experience-Based Co-Design (EBCD) — a framework from healthcare research whose core principle is to treat lived experience as the primary design material, not just input you collect and then set aside. That meant beginning not with "what features should we add?" but with <Hl color="blue">"what is it actually like to encounter 3D data without sight?"</Hl>
          </p>
          <p className="cs-reveal" style={prose}>
            To answer that, I built two design probes: a physical tactile model and an updated digital prototype. Both were brought to two formal sessions, 10 days apart.
          </p>

          {/* Team */}
          <h3 className="cs-reveal" style={{ ...h3, marginTop: '2rem' }}>The team</h3>
          <div className="cs-reveal" style={{ overflowX: 'auto', marginTop: '0.8rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead><tr style={{ background: '#f5f5f7' }}>
                <th style={th}>Name</th><th style={th}>Vision</th><th style={th}>Background</th><th style={th}>Key contribution</th>
              </tr></thead>
              <tbody>
                {[
                  ['Sanchita (me)', 'Corrected 20/20', 'Lead developer + researcher, PhD candidate', 'Built all probes; organized all sessions; implemented all features.'],
                  ['Aziz', 'No vision left; 20/2000 right', 'Graduate researcher, congenital (28)', 'Daily real-time feedback; primary testing partner throughout both phases.'],
                  ['Venkatesh', 'Totally blind', 'Asst. Professor, 7 yrs in data viz accessibility (32, congenital)', 'Proposed stereo panning in Session 1.'],
                  ['Sile', 'Some light perception', 'Professor of Information, 25 yrs teaching (59, congenital)', 'Validated reference sonification; proposed sequential buffer playback.'],
                  ['Ken', 'Totally blind', 'Senior Software Engineer, R&D in assistive tech (55, onset 20)', 'Raised dual-dataset overlay concept; proposed axis convention correction.'],
                  ['JooYoung', 'Light/shape perception only', 'Asst. Professor, creator of MAIDR (35, onset 11)', 'Proposed buffer concept, volumetric audio, and jump-to-peak.'],
                ].map((row, i) => (
                  <tr key={row[0]} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {row.map((cell, j) => <td key={j} style={{ ...td, fontWeight: j === 0 ? 700 : 'normal' }}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Timeline */}
          <h3 className="cs-reveal" style={{ ...h3, marginTop: '2.5rem' }}>The research timeline</h3>
          <div style={{ position: 'relative', marginTop: '1.2rem' }}>
            <div style={{ position: 'absolute', left: 15, top: 20, bottom: 20, width: 2, background: 'linear-gradient(to bottom, #0071e3, #7c3aed, #16a34a)', opacity: 0.25, zIndex: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                { p: 'Phase 1: Foundation', d: 'May – Jun 2025', color: '#0071e3', b: 'Daily collaboration with Aziz. Weekly sessions with JooYoung. Every feature in the ASSETS paper came from this phase. The entire system architecture was established here.' },
                { p: 'Thrust 1: Physical probe', d: 'July 2025', color: '#7c3aed', b: 'I built a tactile 3D surface model from scratch: polystyrene foam base, plastic tubes cut to data heights, aluminum foil ball markers at peaks, a muslin cloth draped as the surface mesh, braille labels at all axes and key landmarks.' },
                { p: 'Thrust 2: Digital probe update', d: 'July 2025', color: '#0071e3', b: 'I integrated JooYoung\'s Phase 1 feedback before the expanded team joined. They started from an already-improved system — not a baseline.' },
                { p: 'Session 1: The experiential gap', d: '12 Aug 2025', color: '#dc2626', b: 'All six researchers. Feel the physical probe first, then navigate the digital version. The gap between simultaneous tactile exploration and sequential digital navigation surfaced immediately. Brainwriting produced consensus on four features.' },
                { p: 'Interim: Three new features', d: 'Aug 2025 (10 days)', color: '#0071e3', b: 'I implemented: reference sonification (0-key), stereo + volumetric audio, configurable buffer. All three traced directly to Session 1 quotes.' },
                { p: 'Session 2: Validation', d: '22 Aug 2025', color: '#16a34a', b: 'Each new feature tested against specific analytic tasks. Second half: embodied and VR future directions. The AI-scoped-to-buffer concept emerged here, unexpectedly.' },
              ].map(p => (
                <div key={p.p} className="cs-reveal" style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: p.color, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 0 4px ${p.color}22` }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />
                  </div>
                  <div className="cs-step" style={{ background: '#f9f9fb', borderRadius: 12, padding: '0.9rem 1.1rem', flex: 1, cursor: 'default' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: p.color }}>{p.p}</div>
                    <div style={{ fontSize: '0.74rem', color: '#bbb', margin: '0.1rem 0 0.4rem' }}>{p.d}</div>
                    <p style={{ fontSize: '0.84rem', color: '#555', margin: 0, lineHeight: 1.55 }}>{p.b}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Session 1 quotes */}
          <h3 className="cs-reveal" style={{ ...h3, marginTop: '2.5rem' }}>Session 1: Quote → Feature</h3>
          <p className="cs-reveal" style={prose}>
            Every feature added in the CHI phase traces back to a direct quote. <Hl color="yellow">Not a summary. Not a theme. A specific sentence from a specific person</Hl> that identified a specific gap.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(255px, 1fr))', gap: '1rem', margin: '1.2rem 0' }}>
            {[
              { who: 'Ken', said: '"Yeah, I find that kind of odd. I would expect the y-axis to be on the board and the z-axis pointing out."', feature: 'Reference sonification (0-key)' },
              { who: 'Venkatesh', said: '"It would be nice to have it pan from left to right, and decrease the volume as it\'s going forward."', feature: 'Stereo panning (X) + volumetric audio (Z)' },
              { who: 'JooYoung', said: '"It might be instrumental if we have a custom buffer, so we can save one specific segment... and then you can hear that buffer."', feature: 'Configurable buffer (D + G)' },
            ].map((q, i) => (
              <div key={q.who} className={`cs-reveal d${i + 1} cs-quote-card`}
                style={{ background: '#fff', borderRadius: 14, padding: '1.6rem', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', borderLeft: '4px solid #0071e3' }}>
                <p style={{ fontStyle: 'italic', color: '#1d1d1f', marginBottom: '0.6rem', lineHeight: 1.58, fontSize: '0.92rem' }}>"{q.said}"</p>
                <div style={{ fontWeight: 800, fontSize: '0.8rem', color: '#0071e3', marginBottom: '0.4rem' }}>{q.who}</div>
                <div style={{ background: '#eef4ff', borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.78rem', color: '#0052cc', fontWeight: 600, display: 'inline-block' }}>
                  Built: {q.feature}
                </div>
              </div>
            ))}
          </div>

          {/* Session 2 */}
          <h3 className="cs-reveal" style={{ ...h3, marginTop: '2.5rem' }}>Session 2: Validation results</h3>
          <div className="cs-reveal" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginTop: '1rem' }}>
              <thead><tr style={{ background: '#f5f5f7' }}>
                <th style={th}>Feature</th><th style={th}>Analytic task tested</th><th style={th}>Result</th><th style={th}>Next iteration</th>
              </tr></thead>
              <tbody>
                {[
                  ['Reference sonification (0-key)', '3D orientation', 'Validated. Sile: "it does help... allowing you to play either coincidentally or in sequence, that\'s really nice."', 'None needed.'],
                  ['Stereo panning', 'Gradient tracing', 'Validated. Sile: "(stereo panning) it actually has been very interesting."', 'Volume alone insufficient for Z depth; JooYoung proposed convolution reverb with pre-delay.'],
                  ['Configurable buffer (D + G)', 'Local maxima vs. global trend', 'Concept validated. Key use: comparing aggregated mean against individual points.', 'Two-step selection needs simplification; boundary announcements needed.'],
                ].map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {row.map((cell, j) => <td key={j} style={td}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cs-reveal" style={{ ...lightbulbBox, marginTop: '2rem' }}>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>💡</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.4rem' }}>An unexpected discovery — spatially scoped AI</div>
            <p style={{ fontSize: '0.95rem', color: '#444', lineHeight: 1.65, marginBottom: '0.4rem' }}>
              Venkatesh: "If I'm looking at just this peak, <strong>I want to ask the AI about just this peak</strong>, not the whole dataset."
            </p>
            <p style={{ fontSize: '0.88rem', color: '#666', margin: 0, lineHeight: 1.6 }}>
              A buffer selection now scopes a Gemini 2.5 Pro query to the exact region of data the user navigated to. Keyboard navigation informing AI context. Nobody planned this. It emerged from the session.
            </p>
          </div>

          <div className="cs-reveal" style={{ marginTop: '1.8rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <a href="https://doi.org/10.1145/3772318.3791272" target="_blank" rel="noopener noreferrer" style={btnOutline}>Read the CHI Paper &rarr;</a>
          </div>
        </div>
      </section>

      {/* ════════════════════ FULL FEATURE SET (after both papers) ════════════════════ */}
      <section style={secGray}>
        <div className="container">
          <span className="cs-reveal" style={pill}>Complete Feature Set</span>
          <h2 className="cs-reveal" style={h2}>Everything in the system — across both papers</h2>
          <p className="cs-reveal" style={prose}>
            <Hl color="blue">17 keyboard shortcuts</Hl> across the two versions. 10 shipped with ASSETS. 7 more added after the CHI sessions. Every single one has a direct quote behind it.
          </p>
          <FeatureTabs />
          <h3 className="cs-reveal" style={{ ...h3, marginTop: '2rem' }}>Full keybinding reference</h3>
          <KeyTable />
        </div>
      </section>

      {/* ════════════════════ ANALYTIC TASKS ════════════════════ */}
      <section style={secWhite}>
        <div className="container">
          <span className="cs-reveal" style={pill}>Analytic Tasks</span>
          <h2 className="cs-reveal" style={h2}>What BLV researchers actually need to do with 3D data</h2>
          <p className="cs-reveal" style={prose}>
            Most accessibility work tests whether users can operate a tool. This work asked a harder question: can BLV researchers <Hl color="yellow">perform the same analytic tasks</Hl> a sighted researcher performs with a 3D visualization? Here is how DIXTRAL addresses each one.
          </p>
          <div className="cs-reveal" style={{ overflowX: 'auto', marginTop: '1.2rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead><tr style={{ background: '#f5f5f7' }}>
                <th style={th}>Task</th><th style={th}>What it requires</th><th style={th}>DIXTRAL feature</th>
              </tr></thead>
              <tbody>
                {[
                  ['3D Orientation', 'Knowing which direction is which axis. Maintaining a coherent spatial frame.', 'Reference sonification (0-key), tri-axis nav (N), axis announcement on switch'],
                  ['Landmark and Peak Finding', 'Locating local maxima and minima without scanning the whole surface.', 'Jump-to-peak (J): outlier detection cycles top/bottom peaks with distinct tones'],
                  ['Local vs. Global Trends', 'Distinguishing a single spike from a broad rising trend across the surface.', 'Configurable buffer (D + G): save a region, compare its mean to individual points'],
                  ['Gradient Tracing', 'Following the path of steepest ascent or descent.', 'Stereo panning (X) + volumetric reverb (Z) — directionality encoded in audio'],
                  ['Occluded Features', 'Detecting values hidden behind taller data points in the visual representation.', 'Tri-axis nav: approach the same point from any of three axis directions'],
                ].map((row, i) => (
                  <tr key={row[0]} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9fb' }}>
                    <td style={{ ...td, fontWeight: 700, color: '#0071e3', whiteSpace: 'nowrap' }}>{row[0]}</td>
                    <td style={td}>{row[1]}</td>
                    <td style={{ ...td, fontSize: '0.81rem', color: '#666' }}>{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ════════════════════ IMPACT ════════════════════ */}
      <section style={secGray}>
        <div className="container">
          <span className="cs-reveal" style={pill}>Impact</span>
          <h2 className="cs-reveal" style={h2}>So what did this actually produce?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem', marginTop: '1.2rem' }}>
            {[
              { icon: '📄', t: 'Two peer-reviewed publications', b: 'ASSETS and CHI are the two premier venues in accessibility and HCI. Both accepted through full peer review.' },
              { icon: '🌐', t: 'Open-source, browser-native tool', b: 'Full system on GitHub with a live demo. Any BLV researcher with a browser can use it today. No hardware, no installation.' },
              { icon: '🔁', t: 'A reusable co-design protocol', b: 'The CHI paper documents exactly how to transfer knowledge from a physical tactile probe into a digital interaction — with all session materials. Other researchers can replicate this.' },
              { icon: '🧩', t: 'Concrete validated design guidance', b: 'Reference sonification. Stereo panning for X, volumetric audio for Z. Configurable buffer for comparison. Not suggestions — validated findings from expert BLV researchers.' },
              { icon: '🤖', t: 'Spatially scoped AI queries', b: 'The buffer region defines what the AI reasons about. Keyboard navigation directly informing large language model context. A novel interaction pattern.' },
              { icon: '🥽', t: 'A grounded path toward VR', b: 'Session 2 produced expert-grounded proposals for haptic, mixed reality, and embodied extensions. Part of my ongoing doctoral thesis.' },
            ].map((item, idx) => (
              <div key={item.t} className={`cs-reveal d${(idx % 3) + 1}`} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ fontSize: '1.5rem', flexShrink: 0, paddingTop: '0.1rem' }}>{item.icon}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.3rem' }}>{item.t}</div>
                  <p style={{ fontSize: '0.86rem', color: '#555', margin: 0, lineHeight: 1.6 }}>{item.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ REFLECTIONS ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 760 }}>
          <span className="cs-reveal" style={pill}>Reflections</span>
          <h2 className="cs-reveal" style={h2}>What I actually learned</h2>

          <blockquote className="cs-reveal cs-pull-quote">
            Every constraint imposed by designing for BLV users{' '}
            <em>produced a better system for everyone.</em>
          </blockquote>

          <p className="cs-reveal" style={prose}>
            The modular architecture exists because <Hl color="yellow">daily co-design demanded rapid iteration</Hl>. The multi-dimensional sonification exists because single-dimension audio is genuinely ambiguous. The tri-axis navigation exists because there is no single "correct" way to think spatially.
          </p>
          <p className="cs-reveal" style={prose}>
            JooYoung and Aziz were not test subjects for my ideas. They were <Hl color="blue">the people who told me my ideas were wrong</Hl> and what to replace them with. The CHI team were not participants. They were expert researchers who knew things about non-visual spatial cognition that no sighted developer could have guessed from the outside.
          </p>
          <div className="cs-reveal" style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.6rem', marginTop: '1.5rem', borderLeft: '4px solid #0071e3' }}>
            <p style={{ fontWeight: 700, color: '#0071e3', marginBottom: '0.6rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>The lesson that shaped everything</p>
            <p style={{ fontSize: '1rem', color: '#1d1d1f', lineHeight: 1.72, margin: 0 }}>
              Accessibility means choosing who your system is <em>for</em> before you write the first line of code. If you add it later, you are retrofitting. If you start with it, <Hl color="yellow">you build a better system</Hl> — not a more compliant one. Better.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════ FUTURE ════════════════════ */}
      <section style={{ background: '#f0f4ff', padding: '3.5rem 0' }}>
        <div className="container">
          <span className="cs-reveal" style={pill}>What Comes Next</span>
          <h2 className="cs-reveal" style={h2}>Open questions</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '1.2rem' }}>
            {[
              { t: 'Formal user studies', b: 'Both papers worked with expert BLV researchers. Testing with novice users and varied technical backgrounds is the next validation step.' },
              { t: 'Reverb-based depth', b: 'Volume alone was insufficient for Z-axis depth. JooYoung proposed convolution reverb with 10–90ms pre-delay scaling with depth. Ready to implement.' },
              { t: 'Dual dataset overlay', b: 'Ken raised comparing two surfaces simultaneously with distinct timbres. Identified in Session 1, not yet built.' },
              { t: 'More 3D data types', b: 'Volumetric data, vector fields, and time-varying visualizations are the next structural targets.' },
              { t: 'Embodied and VR versions', b: 'Session 2 produced grounded proposals for haptic feedback and mixed reality overlays. This is the direction of my doctoral thesis.' },
            ].map((f, i) => (
              <div key={f.t} className={`cs-reveal d${(i % 3) + 1}`} style={{ background: '#fff', borderRadius: 12, padding: '1.1rem', border: '1px solid #dbeafe' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.35rem', color: '#1d1d1f' }}>{f.t}</div>
                <p style={{ fontSize: '0.84rem', color: '#555', margin: 0, lineHeight: 1.5 }}>{f.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ PAPERS FOOTER ════════════════════ */}
      <section style={{ background: '#0a0a0a', padding: '5rem 0', textAlign: 'center' }}>
        <div className="container">
          <h2 className="cs-reveal" style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', marginBottom: '0.6rem', letterSpacing: '-0.02em' }}>Read the papers</h2>
          <p className="cs-reveal" style={{ color: '#666', maxWidth: 400, margin: '0 auto 2.5rem', fontSize: '0.92rem' }}>Both open access via ACM Digital Library.</p>
          <div className="cs-reveal" style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '2.5rem' }}>
            {[
              { venue: 'ASSETS 2025', sub: 'Denver, CO', title: 'Explore, Listen, Inspect: Supporting Multimodal Interaction with 3D Surface and Point Data Visualizations', doi: 'doi.org/10.1145/3663547.3759765', href: 'https://doi.org/10.1145/3663547.3759765' },
              { venue: 'CHI 2026', sub: 'Barcelona, Spain', title: 'Three Modalities, Two Design Probes, One Prototype, and No Vision: Experience-Based Co-Design of a Multi-modal 3D Data Visualization Tool', doi: 'doi.org/10.1145/3772318.3791272', href: 'https://doi.org/10.1145/3772318.3791272' },
            ].map(p => (
              <div key={p.venue} style={{ background: '#141414', borderRadius: 14, padding: '2rem', maxWidth: 320, textAlign: 'left', border: '1px solid #222' }}>
                <div style={{ color: '#60a5fa', fontWeight: 800, marginBottom: '0.2rem', fontSize: '0.9rem' }}>{p.venue}</div>
                <div style={{ color: '#555', fontSize: '0.75rem', marginBottom: '0.7rem' }}>{p.sub}</div>
                <p style={{ color: '#aaa', fontSize: '0.88rem', lineHeight: 1.6, marginBottom: '1rem' }}>{p.title}</p>
                <a href={p.href} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontSize: '0.82rem' }}>{p.doi}</a>
              </div>
            ))}
          </div>
          <div className="cs-reveal" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <a href="https://sanchitakamath.com/dixtral-assets2025-demo/" target="_blank" rel="noopener noreferrer"
              style={{ background: '#0071e3', color: '#fff', fontWeight: 700, padding: '0.8rem 2rem', borderRadius: 8, textDecoration: 'none', fontSize: '0.95rem' }}>
              Try the Live Demo &rarr;
            </a>
            <a href="https://github.com/SK-143381/dixtral-assets2025-demo" target="_blank" rel="noopener noreferrer"
              style={{ border: '1.5px solid #333', color: '#999', padding: '0.8rem 2rem', borderRadius: 8, textDecoration: 'none', fontSize: '0.95rem' }}>
              GitHub (Open Source)
            </a>
          </div>
        </div>
      </section>

      <section style={{ background: '#f5f5f7', padding: '1.8rem 0', textAlign: 'center' }}>
        <div className="container">
          <Link to="/" style={{ color: '#0071e3', fontWeight: 500, fontSize: '0.92rem' }}>&larr; Back to Home</Link>
        </div>
      </section>
    </div>
  );
};

/* ─────────────────────────────────────────────
   SHARED STYLE TOKENS
───────────────────────────────────────────── */
const badge = (bg: string, color: string): React.CSSProperties => ({
  background: bg, color, fontSize: '0.77rem', fontWeight: 600,
  padding: '0.22rem 0.65rem', borderRadius: 20,
});
const pill: React.CSSProperties = {
  display: 'inline-block', background: '#e0edff', color: '#0052cc',
  fontSize: '0.76rem', fontWeight: 700, padding: '0.2rem 0.65rem',
  borderRadius: 20, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em',
};
const btnOutline: React.CSSProperties = {
  display: 'inline-block', padding: '0.55rem 1.15rem',
  border: '1.5px solid #0071e3', color: '#0071e3',
  borderRadius: 8, fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none',
};
const btnOutlineGray: React.CSSProperties = {
  display: 'inline-block', padding: '0.55rem 1.15rem',
  border: '1.5px solid #bbb', color: '#666',
  borderRadius: 8, fontSize: '0.88rem', fontWeight: 500, textDecoration: 'none',
};
const secWhite: React.CSSProperties = { background: '#fff', padding: '5rem 0' };
const secGray: React.CSSProperties  = { background: '#f5f5f7', padding: '5rem 0' };
const h2: React.CSSProperties = {
  fontSize: 'clamp(1.6rem, 3vw, 2rem)', fontWeight: 800,
  letterSpacing: '-0.03em', color: '#1d1d1f', marginBottom: '1rem',
};
const h3: React.CSSProperties = {
  fontSize: '1.1rem', fontWeight: 700, color: '#1d1d1f', marginBottom: '0.7rem',
};
const prose: React.CSSProperties = {
  fontSize: '1.02rem', color: '#444', lineHeight: 1.78, marginBottom: '0.9rem',
};
const lightbulbBox: React.CSSProperties = {
  background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 14,
  padding: '1.6rem 1.8rem', margin: '1.8rem 0',
};
const problemBadge: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
  background: '#1d1d1f', color: '#fff', fontSize: '0.78rem', fontWeight: 700,
  letterSpacing: '0.08em', textTransform: 'uppercase',
  padding: '0.3rem 0.9rem', borderRadius: 20, marginBottom: '1rem',
};
const codeBlock: React.CSSProperties = {
  background: '#111', borderRadius: 12, padding: '1.3rem 1.6rem',
  fontFamily: 'monospace', fontSize: '0.83rem', color: '#e2e8f0',
  lineHeight: 2, margin: '1.5rem 0', border: '1px solid #222',
};
const th: React.CSSProperties = {
  padding: '0.7rem 0.9rem', textAlign: 'left', fontWeight: 700,
  fontSize: '0.82rem', color: '#555', borderBottom: '2px solid #e5e7eb',
};
const td: React.CSSProperties = {
  padding: '0.7rem 0.9rem', fontSize: '0.84rem', color: '#444',
  borderBottom: '1px solid #f0f0f0', verticalAlign: 'top', lineHeight: 1.55,
};

export default DIXTRALCaseStudy;
